import { CaretLeft, Minus, Plus } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import Price from "components/common/Price";
import { INVENTORY_LIMIT_MESSAGE, useDiscountAchieverContext } from "lib/contexts/DiscountAchieverProvider";
import { DiscountAchieverVariant, getInventoryCap } from "lib/types/discountAchiever";
import { truncateString } from "lib/utils/helpers";
import { infoToast } from "lib/utils/toasters";
import { getDefaultProductImage, getVariantImage } from "lib/utils/variantImage";
import { useEffect, useState } from "react";
interface VariantSelectionStepProps {
  onBack: () => void;
}

// Third tray view — variant picker sized to morph inside FinalFamilyTray (no Sheet wrapper).
// updateQty commits immediately (no batched submit — add-to-cart happens later on "Add Selected"),
// so "Confirm" just returns to the previous view.
export const VariantSelectionStep = ({ onBack }: VariantSelectionStepProps) => {
  const { state, actions } = useDiscountAchieverContext();
  const product = state.activeDetailProduct;
  const blockIndex = state.activeBlockIndex;
  const selectedItems = state.blocks[blockIndex]?.selectedItems ?? {};

  const variants = product?.variants ?? [];
  const productName = product?.name ?? "";
  const defaultImage = getDefaultProductImage(product?.images) ?? "";

  const [draft, setDraft] = useState<Record<string, number>>(() => ({ ...selectedItems }));
  useEffect(() => {
    setDraft({ ...(state.blocks[blockIndex]?.selectedItems ?? {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.product_id]);

  const stageQty = (variant: DiscountAchieverVariant, delta: number) => {
    const current = draft[variant.variant_id] ?? 0;
    if (delta > 0 && current >= getInventoryCap(variant)) {
      infoToast(INVENTORY_LIMIT_MESSAGE);
      return;
    }
    const next = Math.max(0, current + delta);
    setDraft((prev) => {
      const copy = { ...prev };
      if (next === 0) delete copy[variant.variant_id];
      else copy[variant.variant_id] = next;
      return copy;
    });
  };

  // Commit draft→selection deltas, then return
  const handleConfirm = () => {
    const ids = new Set([...Object.keys(selectedItems), ...Object.keys(draft)]);
    ids.forEach((variantId) => {
      const delta = (draft[variantId] ?? 0) - (selectedItems[variantId] ?? 0);
      if (delta !== 0) actions.updateQty(blockIndex, variantId, delta);
    });
    onBack();
  };

  if (!product || !variants.length) return null;

  return (
    <div className="relative flex h-[var(--tray-h-variant-selection,520px)] flex-col bg-white">
      {/* Header — pinned */}
      <div className="flex w-full shrink-0 flex-row items-center gap-2 border-b border-gray-light px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          className="rounded-[10px] bg-black/[0.06] p-1.5"
          aria-label="Go back">
          <Icon IconComponent={CaretLeft} size="md" iconProps={{ weight: "bold", className:"text-zinc-500" }} />
        </button>
        <div className="flex flex-col gap-0.5">
          <p className="text-text-xs-medium text-gray-dark">{truncateString(productName, 40)}</p>
          <p className="text-text-sm-semibold text-coal-dark">Choose Option</p>
        </div>
      </div>

      {/* Body — scrolls */}
      <div className="flex-1 overflow-y-auto scrollbar-hide pt-4">
        <ul className="flex flex-col space-y-2 px-3" role="list">
          {variants.map((variant, index) => {
            const itemQty = draft[variant.variant_id] ?? 0;;
            const price = variant.discounted_price ?? variant.current_price;
            const compareAtPrice = variant.original_price > price ? variant.original_price : undefined;
            const isAtMax = itemQty >= getInventoryCap(variant);
            const variantImage = getVariantImage(product?.images, variant.variant_id, defaultImage);
            return (
              <li
                key={variant.variant_id || `variant-${index}`}
                className="flex w-full snap-start flex-col gap-3 py-2">
                <div className="flex flex-row space-x-2">
                  <img
                    src={variantImage}
                    alt={`${productName} - ${variant.name}`}
                    className="h-[5rem] min-h-[5rem] w-[5rem] min-w-[5rem] rounded-lg border border-gray-light object-cover"
                    loading="lazy"
                  />
                  <div className="flex w-full flex-col space-y-1">
                    <div className="flex flex-col space-y-1">
                      <p className="mt-1 text-sm font-medium text-coal-dark break-words">
                        {truncateString(productName, 40)}
                      </p>
                      <div className="flex flex-row space-x-1 text-xs font-medium text-coal-dark">
                        <span>{truncateString(variant.name, 40)}</span>
                      </div>
                    </div>
                    <Price total={price} compareAt={compareAtPrice} orientation="horizontal" />
                    {variant.out_of_stock && <span className="text-sm font-medium text-ouch">Out of stock</span>}
                  </div>
                  <div className="flex flex-col items-center justify-center">
                    {itemQty > 0 ? (
                      <div className="flex flex-row items-center justify-between space-x-1.5 rounded-md border-2 border-primary-dark px-2.5 py-1">
                        <button onClick={() => stageQty(variant, -1)}>
                          <Minus className="h-4 w-4" />
                        </button>
                        <label className="min-w-[1rem] text-center text-sm">{itemQty}</label>
                        <button
                          aria-disabled={isAtMax}
                          onClick={() => stageQty(variant, 1)}
                          className={isAtMax ? "text-gray-light" : "text-coal-dark"}>
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        disabled={variant.out_of_stock}
                        className="rounded-md bg-primary-dark px-4 py-1 text-btnPrimaryText disabled:bg-gray-light"
                        onClick={() => stageQty(variant, 1)}>
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Footer — pinned */}
      <div
        className="z-10 shrink-0 bg-white px-4 pb-4 pt-3"
        style={{ boxShadow: "0px -4px 16px rgba(0, 0, 0, 0.06)" }}>
        <button
          type="button"
          onClick={handleConfirm}
          className="w-full rounded-xl bg-primary-dark py-4 text-text-md-semibold text-btnPrimaryText transition-transform active:scale-[0.98]">
          Confirm
        </button>
      </div>
    </div>
  );
};
